import { Head, Link } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
// lucide-react dropped its brand icons, so there is no Facebook/YouTube
// glyph — About.tsx uses Globe and Video for the same links.
import { Mail, Globe, ExternalLink, Video, MessageCircle } from "lucide-react"

type Props = {
  contact_email: string
  facebook_url: string
  blog_url: string
  youtube_url: string
  x_url: string
}

const title = "Contact"
const description =
  "Get in touch with the Australian Music Database — corrections, missing artists, gig history, chart data, takedown requests, or general questions."

export default function Contact({ contact_email, facebook_url, blog_url, youtube_url, x_url }: Props) {
  const channels = [
    {
      icon: Mail,
      label: "Email",
      value: contact_email,
      href: `mailto:${contact_email}`,
      note: "Best for corrections, additions, and anything detailed.",
    },
    {
      icon: Globe,
      label: "Facebook page",
      value: "Australian Music History",
      href: facebook_url,
      note: "Daily posts, and quick questions get answered here.",
      external: true,
    },
    {
      icon: MessageCircle,
      label: "X",
      value: "@AusMusicHistory",
      href: x_url,
      note: "The daily \"on this day\" roundup posts here too.",
      external: true,
    },
    {
      icon: ExternalLink,
      label: "Australian Music History",
      value: "australianmusichistory.com",
      href: blog_url,
      note: "The sister blog — long-form articles and features.",
      external: true,
    },
    {
      icon: Video,
      label: "YouTube",
      value: "@AusMusicHistory17",
      href: youtube_url,
      note: "Video essays and archival footage.",
      external: true,
    },
  ]

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicShell>
        <div className="max-w-3xl mx-auto py-4 space-y-10 break-words">

          <section>
            <h1 className="mb-4">Get in touch</h1>
            <p>
              The Australian Music Database is maintained by one person, and a lot of what's in it
              arrived because someone took the time to write in. Corrections, missing artists, gig
              history, chart data, photographs — all of it helps, and contributions are credited.
            </p>
          </section>

          <section>
            <h2>How to reach us</h2>
            <ul className="space-y-3 mt-4">
              {channels.map(({ icon: Icon, label, value, href, note, external }) => (
                <li key={href}>
                  <a
                    href={href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    className="flex items-start gap-3 p-4 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl hover:border-amber-300 dark:hover:border-amber-700 transition-colors no-underline group"
                  >
                    <Icon className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <span className="block text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">{label}</span>
                      <span className="block text-sm text-amber-600 dark:text-amber-400 mt-0.5">{value}</span>
                      <span className="block text-xs text-stone-500 dark:text-stone-400 mt-1">{note}</span>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2>What to include</h2>
            <p>A few things make a correction much quicker to act on:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>a link to the page you're referring to;</li>
              <li>what's wrong and what it should say;</li>
              <li>where the information comes from, if you have a source — a gig poster, ticket stub, press clipping or album sleeve is ideal.</li>
            </ul>
            <p className="mt-4">
              If you have an account you can also edit most records directly. All changes are logged.
            </p>
          </section>

          <section>
            <h2>Adding a band or artist</h2>
            <p>
              Send the name along with whatever you have — members and the instruments they played,
              recordings, gig history, chart placings. There's no genre restriction and no minimum
              profile; if they're Australian and made music, they belong here.
            </p>
          </section>

          <section>
            <h2>Copyright and takedown</h2>
            <p>
              If you hold rights to a photograph or other material published here and want it removed,
              email us with the page and what you hold rights to, and it will be taken down. See our{" "}
              <Link href="/terms">Terms of Use</Link> for the full position.
            </p>
          </section>

          <section>
            <h2>Privacy requests</h2>
            <p>
              To access, correct or delete your account information, see{" "}
              <Link href="/data-deletion">Data Deletion</Link> and our{" "}
              <Link href="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2>Response times</h2>
            <p>
              This is a one-person project run alongside other work, so replies aren't instant — but
              every message is read. If something is time-sensitive, the{" "}
              <a href={facebook_url} target="_blank" rel="noopener noreferrer">Facebook page</a> is
              usually the fastest way to get a response.
            </p>
          </section>

        </div>
      </PublicShell>
    </>
  )
}
